import React, { useState } from 'react';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { CustomizeFieldsMenu } from '../components/CustomizeFieldsMenu';
import { useNavigate } from 'react-router-dom';
import { FilePlus2Icon } from 'lucide-react';
import { NewLoanModal } from '../components/NewLoanModal';
import { useLeads } from '../contexts/LeadsContext';
import { useVisibleFields } from '../hooks/useVisibleFields';
import { defaultLeadFieldIds, leadFields } from '../data/fieldCatalog';
import { leadStages, type Lead } from '../data/mockData';
const filters = ['All', 'New', 'Contacting', 'Engaged', 'App Started'] as const;
export function Leads() {
  const [active, setActive] = useState<(typeof filters)[number]>('All');
  const [view, setView] = useState<ViewMode>('list');
  const [newLoanLead, setNewLoanLead] = useState<Lead | null>(null);
  const navigate = useNavigate();
  const {
    leads: queue
  } = useLeads();
  const {
    fieldIds,
    toggle,
    move,
    reset
  } = useVisibleFields('uml.leads.fields', defaultLeadFieldIds);
  const columns = fieldIds.map(id => leadFields.find(field => field.id === id)).filter((field): field is (typeof leadFields)[number] => Boolean(field));
  const rows = active === 'All' ? queue : queue.filter(lead => lead.stage === active);
  return <div className="space-y-4" data-id="element-807" data-mp="3jls93pld7850">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-808" data-mp="3jkh4xoo0bydd">
        <div data-id="element-809" data-mp="3em5t58e5jdsi">
          <h1 className="text-[22px] font-bold text-ink" data-id="element-810" data-mp="3emp1bxe02ntc">Leads</h1>
          <p className="mt-1 text-[13px] text-gray-500" data-id="element-811" data-mp="39m9s9i0r42h6">
            {queue.length} leads in your working list · in-process loans live in their own
            list
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-812" data-mp="30mec0ysc9haa">
          <CustomizeFieldsMenu fields={leadFields} selectedIds={fieldIds} onToggle={toggle} onMove={move} onReset={reset} anchorLabel="Borrower" data-id="element-813" data-mp="30npfexhvgn0v" />
          <ViewToggle value={view} onChange={setView} data-id="element-814" data-mp="ysx7w53f2yka" />
        </div>
      </div>

      {view === 'list' && <div className="flex flex-wrap gap-2" role="group" aria-label="Lead filters" data-id="element-815" data-mp="2x475dwcykcry">
          {filters.map(filter => <button key={filter} type="button" aria-pressed={active === filter} onClick={() => setActive(filter)} className={['rounded-full border px-3.5 py-1.5 text-[12.5px] font-medium transition-colors', active === filter ? 'border-brand bg-brand text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'].join(' ')} data-id="element-816" data-mp="2jdljrzw4doqd">
              {filter}
            </button>)}
        </div>}

      {view === 'list' ? <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-id="element-817" data-mp="1qn3ecs7xbb9s">
          <table className="w-full min-w-[640px] text-left" data-id="element-818" data-mp="1vnhqalcnb5d3">
            <thead className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500" data-id="element-819" data-mp="1vkv9rw7s1fiq">
              <tr data-id="element-820" data-mp="1j4vsi2xmdrjc">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-821" data-mp="1j5dr367r8huf">
                  Borrower
                </th>
                {columns.map(column => <th key={column.id} scope="col" className="px-5 py-2.5 font-semibold" data-id="element-822" data-mp="1s5ai3ehm9ssn">
                    {column.label}
                  </th>)}
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-823" data-mp="14g97plp847kq">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-[13px]" data-id="element-824" data-mp="1e5smaf5wlf9s">
              {rows.map(lead => <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} className="cursor-pointer hover:bg-gray-50" data-id="element-825" data-mp="19drfh8ri8gr5">
                  <td className="px-5 py-3" data-id="element-826" data-mp="gp3r34bglb9d">
                    <div className="font-semibold text-ink" data-id="element-827" data-mp="goarqoetxo19">{lead.name}</div>
                    <div className="text-[12px] text-gray-500" data-id="element-828" data-mp="3g5p9elvw8yxi">{lead.state}</div>
                  </td>
                  {columns.map(column => <td key={column.id} className="px-5 py-3 text-gray-600" data-id="element-829" data-mp="3p52kfizo1vkc">
                      {column.render ? column.render(lead) : column.value(lead)}
                    </td>)}
                  <td className="px-5 py-3 text-right" data-id="element-830" data-mp="2prcsi4kguqr">
                    <button type="button" onClick={event => {
                event.stopPropagation();
                setNewLoanLead(lead);
              }} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-medium text-ink transition-colors hover:border-brand hover:text-brand" data-id="element-831" data-mp="2rc8ib2majq7">
                      <FilePlus2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-832" data-mp="2ihf9ccfo4l91" />
                      Create loan
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
          {rows.length === 0 && <p className="px-5 py-10 text-center text-[13px] text-gray-500" data-id="element-833" data-mp="216s36j6li0uj">
              No leads in this stage right now.
            </p>}
          {columns.length === 0 && <p className="border-t border-gray-100 px-5 py-3 text-[12px] text-gray-500" data-id="element-834" data-mp="2tvqrjy1b9z8a">
              No fields selected — use Fields to add columns.
            </p>}
        </div> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-835" data-mp="2kusi3083szib">
          {leadStages.map(stage => {
        const items = queue.filter(lead => lead.stage === stage);
        return <section key={stage} className="rounded-xl bg-gray-100/70 p-2.5" data-id="element-836" data-mp="19gf18fbrpkbs">
                <header className="flex items-center justify-between px-1 pb-2" data-id="element-837" data-mp="14h2cfp1c636k">
                  <h2 className="text-[12px] font-semibold text-ink" data-id="element-838" data-mp="14j5beykgv6nf">{stage}</h2>
                  <span className="text-[11.5px] text-gray-500" data-id="element-839" data-mp="15yr72e39qc03">{items.length}</span>
                </header>
                <div className="space-y-2" data-id="element-840" data-mp="1auyfcdj4zzl7">
                  {items.map(lead => <button key={lead.id} type="button" onClick={() => navigate(`/leads/${lead.id}`)} className="w-full rounded-xl border border-gray-200 bg-white p-3.5 text-left transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-841" data-mp="wx061mjoatck">
                      <p className="truncate text-[13px] font-semibold text-ink" data-id="element-842" data-mp="dalmpsg4mp0s">
                        {lead.name}
                      </p>
                      <dl className="mt-2 space-y-1" data-id="element-843" data-mp="i612ipxp2uzu">
                        {columns.filter(column => column.id !== 'stage').map(column => <div key={column.id} className="flex items-baseline gap-2" data-id="element-844" data-mp="3lr9wgbfjmysk">
                              <dt className="w-24 shrink-0 truncate text-[10.5px] uppercase tracking-wide text-gray-400" data-id="element-845" data-mp="3lt4glnizqmvl">
                                {column.label}
                              </dt>
                              <dd className="min-w-0 flex-1 truncate text-[12px] text-ink" data-id="element-846" data-mp="3qqe3avezwkfq">
                                {column.value(lead)}
                              </dd>
                            </div>)}
                      </dl>
                    </button>)}
                  {items.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-[11.5px] text-gray-400" data-id="element-847" data-mp="i99miqqfgjcb">
                      Empty
                    </p>}
                </div>
              </section>;
      })}
        </div>}

      <NewLoanModal lead={newLoanLead} onClose={() => setNewLoanLead(null)} onCreated={() => setNewLoanLead(null)} data-id="element-848" data-mp="mdh8wh9exn7d" />
    </div>;
}